import { useEffect } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import FaceMetricsHandler from "./FaceMetricsHandler";
import ObjectDetectionHandler from "./ObjectDetectionHandler";

const FaceDetector = ({
  videoStream,
  dataChannel,
  viewerId,
  metricsHandlerRef,
}) => {
  useEffect(() => {
    let faceLandmarker;
    let isRunning = false;
    let metricsHandler;
    let objectHandler;
    let lastProcessTime = 0;
    let lastObjectDetectionTime = 0;
    const PROCESS_INTERVAL = 100; // Process every 100ms
    let initializeTimeout;
    let videoCheckTimeout;

    const initializeHandlers = () => {
      console.log(
        "[FaceDetector] Data channel state:",
        dataChannel?.readyState
      );
      if (dataChannel && dataChannel.readyState === "open") {
        console.log("[FaceDetector] Initializing handlers with data channel");
        metricsHandler = new FaceMetricsHandler(dataChannel, viewerId);
        if (metricsHandlerRef) {
          metricsHandlerRef.current = metricsHandler;
        }
        objectHandler = new ObjectDetectionHandler(dataChannel, viewerId);
      } else {
        console.log("[FaceDetector] Waiting for data channel to be ready...");
        initializeTimeout = setTimeout(initializeHandlers, 1000);
      }
    };

    const captureFrame = async () => {
      if (!videoStream) return null;

      const canvas = document.createElement("canvas");
      canvas.width = videoStream.videoWidth;
      canvas.height = videoStream.videoHeight;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoStream, 0, 0);

      // Convert to base64 then to binary
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              // Get the binary data as Buffer
              const buffer = reader.result;
              resolve(new Uint8Array(buffer));
            };
            reader.readAsArrayBuffer(blob);
          },
          "image/jpeg",
          0.95
        ); // Convert to JPEG format with 95% quality
      });
    };

    const initializeFaceDetector = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
        );

        faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            delegate: "GPU",
          },
          outputFaceBlendshapes: true,
          runningMode: "VIDEO",
          numFaces: 1,
        });

        console.log("[FaceDetector] Initialized successfully");
        initializeHandlers();
      } catch (error) {
        console.error("[FaceDetector] Initialization error:", error);
      }
    };

    const processBlendShapes = (blendshapes) => {
      if (!blendshapes || !blendshapes[0] || !metricsHandler) return;

      const shapes = {};
      blendshapes[0].categories.forEach((shape) => {
        shapes[shape.categoryName] = shape.score;
      });

      metricsHandler.processBlendshapes(shapes);
    };

    const detectFaces = async () => {
      if (!faceLandmarker || !videoStream || !isRunning) return;

      const now = Date.now();
      if (now - lastProcessTime < PROCESS_INTERVAL) {
        requestAnimationFrame(detectFaces);
        return;
      }

      try {
        // Process face metrics
        const results = await faceLandmarker.detectForVideo(
          videoStream,
          Date.now()
        );

        if (results.faceBlendshapes) {
          processBlendShapes(results.faceBlendshapes);
        } else {
          console.log("[FaceDetector] No face detected");
        }

        // Process object detection more frequently (every 300ms)
        if (objectHandler && now - lastObjectDetectionTime >= 300) {
          const imageBuffer = await captureFrame();
          if (imageBuffer) {
            objectHandler.processImage(imageBuffer);
            lastObjectDetectionTime = now;
          }
        }

        lastProcessTime = now;
      } catch (error) {
        console.error("[FaceDetector] Detection error:", error);
      }

      if (isRunning) {
        requestAnimationFrame(detectFaces);
      }
    };

    const checkVideoReady = () => {
      if (!isRunning) return;

      if (videoStream && videoStream.readyState >= 2) {
        console.log("[FaceDetector] Video stream ready, initializing detector");
        initializeFaceDetector().then(() => {
          if (isRunning) {
            detectFaces();
          }
        });
      } else {
        console.log("[FaceDetector] Video stream not ready, retrying...");
        videoCheckTimeout = setTimeout(checkVideoReady, 100);
      }
    };

    isRunning = true;
    checkVideoReady();

    return () => {
      console.log("[FaceDetector] Cleaning up");
      isRunning = false;

      if (initializeTimeout) clearTimeout(initializeTimeout);
      if (videoCheckTimeout) clearTimeout(videoCheckTimeout);

      if (faceLandmarker) {
        faceLandmarker.close();
      }

      if (metricsHandler) {
        metricsHandler.cleanup();
        metricsHandler = null;
        if (metricsHandlerRef) {
          metricsHandlerRef.current = null;
        }
      }

      if (objectHandler) {
        objectHandler.cleanup();
        objectHandler = null;
      }
    };
  }, [videoStream, dataChannel, viewerId, metricsHandlerRef]);

  return null;
};

export default FaceDetector;
